import React from "react";

import { Grid } from "@material-ui/core";
import { Form, Formik, useFormikContext } from "formik";
import { uniqBy } from "lodash";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import { InputDateRange } from "components/DateRangePicker/dateRangePicker";
import { Button } from "components/Form";
import { SelectField, useSubmitHandler } from "components/Form/FormikControls";
import { Error } from "components/Form/FormikControls/Error";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { BillingRunSummary } from "components/MultiSaleReports/BillingRunSummary";

import { ApiModel } from "constants/loading";
import { ReportQueryParam, Reports } from "constants/reports";
import { SaleTypes } from "constants/sale";

import { getReportUrl } from "lib/reports";
import { formatISO8601DateString } from "lib/timeFormats";

import {
  getActiveRole,
  getRuleBooks,
  getSaleSubTypes,
  selectSaleOptionsV2,
} from "selectors";

const getEndTimeValidation = () => {
  return Yup.date().nullable();
};
const getStartTimeValidation = endTimeFieldName => {
  return Yup.date()
    .nullable()
    .when(endTimeFieldName, endTimeValue => {
      // If we have an end date, compare the two, and make sure it's less than 93 days.
      if (endTimeValue) {
        return Yup.date()
          .required("Start date is required")
          .test(
            "withinRange",
            "Maximum date range of 3 months.",
            createdGte => {
              // Only validate it if it's set, even if it's already nullable.  :'(
              if (!createdGte) {
                return true;
              }
              return endTimeValue - createdGte <= 93 * 24 * 60 * 60 * 1000;
            },
          );
      }
    });
};

function MultiSaleReportFormBody(props) {
  const { reportSlug } = props;
  const { queryMap } = Reports[reportSlug];
  const saleSubTypes = useSelector(getSaleSubTypes);
  const subTypeOptions = uniqBy(
    Object.values(saleSubTypes).map(s => ({
      label: s.name,
      value: s.id,
    })),
    "label",
  );

  const saleTypesFromState = useSelector(
    state => state.saleDefinitions.saleTypes,
  );

  const saleTypes = Object.values(SaleTypes)
    .filter(saleType => saleTypesFromState.includes(saleType))
    .map(saleType => ({
      label: saleType,
      value: saleType,
    }));

  const showLivestockSaleFilter = queryMap[ReportQueryParam.livestockSaleIdIn];

  const showSaleDateFilter =
    queryMap[ReportQueryParam.livestockSaleDateGte] ||
    queryMap[ReportQueryParam.livestockSaleDateLte];

  const showSaleTypeFilter =
    queryMap[ReportQueryParam.livestockSaleSaleTypeNameIn];

  const showSaleSubTypeFilter =
    queryMap[ReportQueryParam.livestockSaleSaleSubTypeIdIn];

  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false);
  useSubmitHandler(true, setIsSubmitEnabled);

  const { setValues, values, errors } = useFormikContext();

  const livestockSaleOptions = useSelector(selectSaleOptionsV2);

  const updateDates = (startDate, endDate) => {
    const updates = {
      livestockSaleDateGte: startDate,
      livestockSaleDateLte: endDate,
    };
    setValues({
      ...values,
      ...updates,
    });
  };

  return (
    <Grid container spacing={2}>
      {errors && errors.anyFields ? (
        <Grid item xs={12}>
          <Error>{errors.anyFields}</Error>
        </Grid>
      ) : null}
      {showSaleDateFilter && (
        <InputDateRange
          updateDates={updateDates}
          startDateFieldName="livestockSaleDateGte"
          endDateFieldName="livestockSaleDateLte"
          includeTime={false}
        />
      )}

      {showLivestockSaleFilter && (
        <Grid item xs={12}>
          <SelectField
            isClearable
            name="livestockSaleIdIn"
            label="Sale"
            options={livestockSaleOptions}
            isMulti
          />
        </Grid>
      )}

      {showSaleTypeFilter && (
        <Grid item xs={12}>
          <SelectField
            label="Sale Type"
            name="livestockSaleSaleTypeNameIn"
            options={saleTypes}
            isClearable
            isMulti
          />
        </Grid>
      )}
      {showSaleSubTypeFilter && (
        <Grid item xs={12}>
          <SelectField
            label="Sale Subtype"
            name="livestockSaleSaleSubTypeIdIn"
            options={subTypeOptions}
            isClearable
            isMulti
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Button data-tour="submit" type="submit" disabled={!isSubmitEnabled}>
          Run Report
        </Button>
      </Grid>
    </Grid>
  );
}

export function MultiSaleReportForm(props) {
  const { reportSlug } = props;

  const { queryMap } = Reports[reportSlug];

  const ruleBookByIdLookup = useSelector(getRuleBooks);

  const initialValues = {
    rulebookId: Object.values(ruleBookByIdLookup)?.[0]?.id,
  };
  const roleSlug = useSelector(state => getActiveRole(state).slug);

  function onSubmit(values) {
    if (values.livestockSaleDateGte) {
      values.livestockSaleDateGte = formatISO8601DateString(
        new Date(values.livestockSaleDateGte),
      );
    }
    if (values.livestockSaleDateLte) {
      values.livestockSaleDateLte = formatISO8601DateString(
        new Date(values.livestockSaleDateLte),
      );
    }
    const reportUrl = getReportUrl(Reports[reportSlug], {
      ...values,
      userRole: roleSlug,
    });
    window.open(reportUrl, "_blank");
  }

  const showLivestockSaleFilter = queryMap[ReportQueryParam.livestockSaleIdIn];

  const showSaleDateFilter =
    queryMap[ReportQueryParam.livestockSaleDateGte] ||
    queryMap[ReportQueryParam.livestockSaleDateLte];

  const showSaleTypeFilter =
    queryMap[ReportQueryParam.livestockSaleSaleTypeNameIn];

  const showSaleSubTypeFilter =
    queryMap[ReportQueryParam.livestockSaleSaleSubTypeIdIn];

  const validationShape = {};

  if (showLivestockSaleFilter) {
    validationShape.livestockSaleidIn = Yup.array()
      .of(Yup.number())
      .nullable();
  }
  if (showSaleDateFilter) {
    validationShape.livestockSaleDateLte = getEndTimeValidation(
      "livestockSaleDateGte",
    );
    validationShape.livestockSaleDateGte = getStartTimeValidation(
      "livestockSaleDateLte",
    );
  }
  if (showSaleTypeFilter) {
    validationShape.livestockSaleSaleTypeNameIn = Yup.array()
      .of(Yup.string())
      .nullable();
  }
  if (showSaleSubTypeFilter) {
    validationShape.livestockSaleSaleSubTypeIdIn = Yup.array()
      .of(Yup.number())
      .nullable();
  }

  const validationSchema = Yup.lazy(() => Yup.object().shape(validationShape));

  return (
    <WaitForSync requiredData={[ApiModel.RULE_BOOKS]}>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <Form data-tour="settings-form">
          <MultiSaleReportFormBody reportSlug={reportSlug} />
          <BillingRunSummary />
        </Form>
      </Formik>
    </WaitForSync>
  );
}
